import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BUILD_PROFILE } from './buildProfile';
import { trackGoogleTagEvent, trackGoogleTagEventWithCallback } from './googleTag';
import { recordEvent } from './observability';
import { trackMetaPixelEvent, trackMicrosoftUetEvent } from './tracking';

/**
 * Hook para rastrear page views automaticamente
 * Dispara evento 'navigation' quando a página muda
 */
export function usePageView(pageName?: string) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const displayName = pageName || path || '/';

    trackGoogleTagEvent('manual_event_PAGE_VIEW', {
      page_path: path,
      page_location:
        typeof window !== 'undefined' ? window.location.href : `${path}${location.search}`,
      page_title: typeof document !== 'undefined' ? document.title || displayName : displayName,
    });

    recordEvent('navigation', {
      page: displayName,
      pathname: path,
      search: location.search,
    });
  }, [location.pathname, location.search, pageName]);
}

/**
 * Track clicks em botões importantes
 * Use em onClick handlers
 */
export function trackClick(buttonName: string, metadata?: Record<string, unknown>): void {
  recordEvent('navigation', {
    action: 'click',
    element: buttonName,
    ...metadata,
  });
}

type MarketingConversionMetadata = Record<string, unknown>;

const buildConversionPayload = (metadata: MarketingConversionMetadata = {}) => ({
  ...metadata,
  brand: BUILD_PROFILE.brand,
  build: BUILD_PROFILE.displayName,
});

export function trackCreateListClick(
  metadata?: MarketingConversionMetadata,
  onComplete?: () => void
): void {
  const payload = buildConversionPayload(metadata);

  trackMetaPixelEvent('CreateListClick', payload);
  trackMicrosoftUetEvent(BUILD_PROFILE.tracking.createListClickEvent, payload);
  trackGoogleTagEventWithCallback(BUILD_PROFILE.tracking.createListClickEvent, payload, onComplete);
}

export function trackCreateListSuccess(metadata?: MarketingConversionMetadata): void {
  const payload = buildConversionPayload(metadata);

  trackGoogleTagEvent(BUILD_PROFILE.tracking.createListSuccessEvent, payload);
  trackMetaPixelEvent('CreateListSuccess', payload);
  trackMicrosoftUetEvent(BUILD_PROFILE.tracking.createListSuccessEvent, payload);
}

/**
 * Track tentativa de login/autenticação
 */
export function trackAuthAttempt(
  provider: 'google' | 'microsoft' | 'guest',
  metadata?: Record<string, unknown>
): void {
  recordEvent('user_login', {
    provider,
    status: 'attempt',
    ...metadata,
  });
}

/**
 * Track erro de autenticação
 */
export function trackAuthError(
  provider: string,
  error: string | Error,
  metadata?: Record<string, unknown>
): void {
  recordEvent('user_login', {
    provider,
    status: 'failed',
    error: typeof error === 'string' ? error : error.message,
    ...metadata,
  });
}

/**
 * Track sucesso de autenticação
 */
export function trackAuthSuccess(
  provider: string,
  email?: string,
  metadata?: Record<string, unknown>
): void {
  recordEvent('user_login', {
    provider,
    status: 'success',
    email,
    ...metadata,
  });
}

/**
 * Track form submission (create lista, edit, etc)
 */
export function trackFormSubmit(
  formName: string,
  success: boolean,
  errorMessage?: string,
  metadata?: Record<string, unknown>
): void {
  recordEvent(success ? 'list_created' : 'page_error', {
    formName,
    status: success ? 'success' : 'failed',
    errorMessage,
    ...metadata,
  });
}

/**
 * Track quando elemento (button, link) é clicável/visível
 * Useful para saber se UI está renderizando
 */
export function trackElementVisible(elementName: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[VISIBLE] ${elementName}`);
  }

  recordEvent('navigation', {
    action: 'element_visible',
    element: elementName,
  });
}
